import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown, MenuProps, Typography } from 'antd'
import Const from 'constants/constant'
import useAuth from 'hooks/useAuth'
import useDirectory from 'hooks/useDirectory'
import { useRecoilState, useRecoilValue } from 'recoil'
import { floorDetailState, isUploadingMapState } from 'store/buildingStore'

import MenuIcon from 'components/icons/MenuIcon'

import ModalDefaultImage from './ModalDefaultImage'
import DeleteFloorModal from './ModalDelete'
import EditNameModal from './ModalEditName'

type Props = {
  floorName: string
}

export default function FloorDropDownMenu({ floorName }: Props) {
  const { profile } = useAuth()
  const { t } = useTranslation()
  const [openedModal, setOpenedModal] = useState('')
  const [, setIsUploadingMap] = useRecoilState(isUploadingMapState)
  const { getDirectory } = useDirectory()
  const floorDetail = useRecoilValue(floorDetailState)

  /**
   * Handles opening different modals based on the provided key.
   * - If the key is 'handleUploadMap' and the floor is not marked as "no map registration" (isUnregisteredMap === false),
   *   it will open the upload map modal.
   * - For all keys, it sets the opened modal state.
   *
   * @param {string} key - The identifier for the modal to open.
   */
  const handleOpenModal = (key: string) => {
    if (key === 'handleUploadMap' && !floorDetail?.isUnregisteredMap) {
      setIsUploadingMap((prev) => ({ ...prev, isOpen: true }))
    }

    setOpenedModal(key)
  }

  const items: MenuProps['items'] = Const.SIDEBAR_DETAIL_FLOOR_MENU.filter(
    (item) => profile?.role && item.role.includes(profile?.role)
  ).map(({ label, key }) => {
    /**
     * Disable upload map if isUnregisteredMap === true.
     * This ensures that when a floor is marked as "no map registration",
     * the upload map option is disabled in the menu.
     */
    const disabled = key === 'handleUploadMap' && floorDetail?.isUnregisteredMap
    return {
      key,
      label: (
        <Typography
          className={`text-white text-[12px] font-[700] whitespace-nowrap ${
            disabled ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          {t(label)}
        </Typography>
      ),
      onClick: () => handleOpenModal(key)
    }
  })

  return (
    <div className="h-full cursor-pointer">
      <Dropdown
        menu={{ items }}
        className="w-auto"
        placement="bottomRight"
        trigger={['click']}
        overlayClassName={`${getDirectory()}-theme`}
        rootClassName="dropdown-custom"
      >
        <div className="h-full flex my-auto pr-[10px]">
          <MenuIcon />
        </div>
      </Dropdown>

      {openedModal === 'handleDelete' && (
        <DeleteFloorModal floorName={floorName} setIsOpen={setOpenedModal} />
      )}

      {openedModal === 'handleEditName' && (
        <EditNameModal setIsOpen={setOpenedModal} />
      )}

      {openedModal === 'handleDefaultImage' && (
        <ModalDefaultImage setIsOpen={setOpenedModal} />
      )}
    </div>
  )
}
